import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/flexkit/Command";
import { containerClassName, inputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { AvatarStack } from "@/components/projects/cards/AvatarStack";
import { userToStackItem } from "@/components/projects/cards/avatar-stack-helpers";
import { SelectEnvironmentClassForProjectModal } from "@/components/projects/SelectEnvironmentClassForProjectModal";
import { formatRepoUrlForDisplay } from "@/format/url";
import { useCreateEnvironmentFromProjectWithAuth } from "@/hooks/use-create-environment-from-project";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { useGetPreferredEnvironmentClassPerProject } from "@/hooks/use-preferred-env-class-per-project";
import { TrackLocations } from "@/hooks/use-segment";
import type { CheckScmAuthForProjectResult } from "@/queries/environment-queries";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import { useMembers } from "@/queries/organization-queries";
import {
    useListProjectEnvironmentClasses,
    useListProjects,
    useListProjectStates,
    type PlainProject,
} from "@/queries/project-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError, isCreditsExhaustionError, isVerifyCardError } from "@/utils/errors";
import { getProjectUsers } from "@/utils/projects";
import { Code, ConnectError } from "@connectrpc/connect";
import Fuse from "fuse.js";
import { CreateEnvironmentFromProjectBadRequestEnvironmentClassDetails } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

const searchOptions = {
    includeScore: true,
    keys: [
        "metadata.name",
        "id",
        {
            name: "contextUrl",
            getFn: (item: PlainProject) => getRepoUrlFromInitializer(item.initializer) ?? "",
        },
    ],
};

export const ProjectTab: FC<{
    onClose: () => void;
}> = ({ onClose }) => {
    const navigate = useNavigate();
    const { toast } = useToast();

    const isSubmittingRef = useRef(false);
    const [projectSearch, setProjectSearch] = useState("");
    const [selectedProject, setSelectedProject] = useState<PlainProject | undefined>();

    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const { data: projectData, isPending: isPendingProjects } = useListProjects();
    const { data: projectsStates, isLoading: isLoadingProjectsStates } = useListProjectStates(
        projectData?.projects.map((p) => p.id),
    );
    const { data: membersData, isPending: isPendingMembers } = useMembers();

    const { preferredEnvironmentClassPerProject, isPending: isPendingPreference } =
        useGetPreferredEnvironmentClassPerProject();

    const { handleCreateEnvironmentFromProject: createEnvironmentFromProjectWithAuth, isLoading: isCreating, isSuccess } =
        useCreateEnvironmentFromProjectWithAuth();

    const [showScmAuthModal, setShowScmAuthModal] = useState<
        (CheckScmAuthForProjectResult & { rememberChoice?: boolean }) | undefined
    >();
    const [showEnvClassSelectorModal, setShowEnvClassSelectorModal] = useState<
        | { project?: PlainProject; filteredEnvironmentClassIds?: string[]; title?: string; description?: string }
        | undefined
    >();

    const { data: projectEnvironmentClasses, isLoading: isLoadingProjectClasses } = useListProjectEnvironmentClasses(
        selectedProject?.id,
    );

    const allProjects = useMemo(() => {
        if (!projectData?.projects) return [];
        return projectData.projects;
    }, [projectData]);

    const projectsSearchIndex = useMemo(() => {
        return Fuse.createIndex(searchOptions.keys, allProjects);
    }, [allProjects]);

    const filteredProjects = useMemo(() => {
        if (!projectSearch) {
            return allProjects;
        }

        const fuse = new Fuse(allProjects, searchOptions, projectsSearchIndex);
        const result = fuse.search(projectSearch);
        return result.map((r) => r.item);
    }, [allProjects, projectsSearchIndex, projectSearch]);

    const isLoading =
        isLoadingProjectsStates ||
        isPendingProjects ||
        isPendingMembers ||
        isLoadingUser ||
        !projectData ||
        !projectsStates ||
        !user;

    const showProjectEnvClassSelector = useMemo(() => {
        return (
            !!selectedProject?.id &&
            !preferredEnvironmentClassPerProject?.get(selectedProject.id) &&
            projectEnvironmentClasses?.environmentClasses &&
            projectEnvironmentClasses.environmentClasses.length > 1
        );
    }, [preferredEnvironmentClassPerProject, selectedProject, projectEnvironmentClasses]);

    const createEnvironment = useCallback(
        async (
            project: PlainProject,
            preferredEnvClassId?: string,
            rememberChoice?: boolean,
            skipAuthCheck?: boolean,
        ) => {
            // Prevent duplicate submissions
            if (isSubmittingRef.current) {
                return;
            }

            isSubmittingRef.current = true;
            try {
                const result = await createEnvironmentFromProjectWithAuth({
                    project,
                    preferredEnvClassId,
                    projectEnvironmentClasses: projectEnvironmentClasses?.environmentClasses || [],
                    rememberChoice,
                    skipAuthCheck,
                });

                if (result?.type === "success") {
                    toast({ title: "Created new environment" });
                    onClose();
                    navigate(getDetailsURL({ environment: result.environment }));
                    setShowScmAuthModal(undefined);
                } else if (result?.type === "authRequired") {
                    setShowScmAuthModal({ ...result.authResult, rememberChoice });
                }
            } catch (e) {
                if (
                    !showProjectEnvClassSelector &&
                    !isVerifyCardError(e) &&
                    e instanceof ConnectError &&
                    (e.code === Code.FailedPrecondition || e.code === Code.NotFound)
                ) {
                    const details = e.findDetails(CreateEnvironmentFromProjectBadRequestEnvironmentClassDetails);
                    setShowEnvClassSelectorModal({
                        project,
                        filteredEnvironmentClassIds:
                            details.length > 0 && details[0].environmentClassIds.length > 0
                                ? details[0].environmentClassIds
                                : undefined,
                        title: "Failed to create environment",
                        description: `${formatError(e)}`,
                    });
                } else if (
                    !isVerifyCardError(e) && // These are handled by the top level verify card check
                    !isCreditsExhaustionError(e) // These are handled by the top level credits exhaustion check
                ) {
                    toast({
                        title: "Failed to create an environment",
                        description: formatError(e),
                    });
                }
            } finally {
                isSubmittingRef.current = false;
            }
        },
        [
            createEnvironmentFromProjectWithAuth,
            navigate,
            onClose,
            showProjectEnvClassSelector,
            toast,
            projectEnvironmentClasses?.environmentClasses,
        ],
    );

    // Effect to handle environment creation after selection and env class check
    useEffect(() => {
        if (!selectedProject || isPendingPreference || isLoadingProjectClasses) {
            return;
        }

        if (showProjectEnvClassSelector) {
            setShowEnvClassSelectorModal({ project: selectedProject });
            setSelectedProject(undefined); // Reset selection
        } else {
            const preferredEnvClassId = preferredEnvironmentClassPerProject?.get(selectedProject.id);
            void createEnvironment(selectedProject, preferredEnvClassId);
            setSelectedProject(undefined); // Reset selection
        }
    }, [
        selectedProject,
        showProjectEnvClassSelector,
        isPendingPreference,
        isLoadingProjectClasses,
        preferredEnvironmentClassPerProject,
        createEnvironment,
    ]);

    if (isLoading) {
        return <LoadingState delay={true} />;
    }

    return (
        <>
            <Command
                className="mt-6 flex grow flex-col border-none px-1.5 pt-1 shadow-none"
                shouldFilter={false}
                data-testid="projects-list"
            >
                <CommandInput
                    placeholder="Search projects..."
                    value={projectSearch}
                    onValueChange={setProjectSearch}
                    containerClassName={cn(containerClassName, "px-0 mx-0.5 border rounded-lg mb-4 *:py-2 *:h-auto")}
                    className={inputClassName}
                    autoFocus
                />
                <CommandList className="size-full max-h-[calc(100vh-22rem)] overflow-y-auto overflow-x-hidden pr-1 *:flex *:flex-col *:gap-2 md:max-h-[calc(100vh-29rem)]">
                    <CommandEmpty className="rounded-lg bg-surface-accent">No projects found</CommandEmpty>
                    {filteredProjects.map((project) => {
                        const projectContextUrl = getRepoUrlFromInitializer(project.initializer);
                        const users = getProjectUsers(project, membersData?.members);
                        const avatarStackItems = users?.map(userToStackItem) ?? [];

                        return (
                            <CommandItem
                                key={project.id}
                                value={project.id}
                                onSelect={() => setSelectedProject(project)}
                                className="cursor-pointer rounded-lg border border-border-light px-3 pb-2.5 pt-3 data-[selected=true]:border-border-brand data-[selected=true]:bg-surface-brand/5"
                                disabled={isCreating || isSuccess || isPendingPreference || isLoadingProjectClasses}
                            >
                                <div className="flex w-full items-center justify-between gap-4">
                                    <div className="min-w-0 grow overflow-hidden">
                                        <div
                                            className="truncate text-base font-medium text-content-primary"
                                            title={project.metadata?.name}
                                        >
                                            {project.metadata?.name}
                                        </div>
                                        <div className="truncate text-sm text-content-muted">
                                            {formatRepoUrlForDisplay(projectContextUrl)}
                                        </div>
                                    </div>
                                    <div className="shrink-0">
                                        <AvatarStack items={avatarStackItems} disablePopover />
                                    </div>
                                </div>
                            </CommandItem>
                        );
                    })}
                </CommandList>
            </Command>

            {showEnvClassSelectorModal?.project && (
                <SelectEnvironmentClassForProjectModal
                    project={showEnvClassSelectorModal.project}
                    filteredEnvironmentClassIds={showEnvClassSelectorModal.filteredEnvironmentClassIds}
                    title={showEnvClassSelectorModal.title}
                    description={showEnvClassSelectorModal.description}
                    onClose={() => setShowEnvClassSelectorModal(undefined)}
                    onSelect={async (environmentClass: EnvironmentClassEntry, rememberChoice: boolean) => {
                        const project = showEnvClassSelectorModal.project;
                        if (project) {
                            setShowEnvClassSelectorModal(undefined);
                            await createEnvironment(project, environmentClass.clazz.id, rememberChoice);
                        }
                    }}
                />
            )}

            {showScmAuthModal && (
                <SCMAuthenticationModal
                    repoURL={getRepoUrlFromInitializer(showScmAuthModal.project.initializer) || ""}
                    authenticationUrl={showScmAuthModal.authenticationResult.authenticationUrl}
                    patAuth={showScmAuthModal.authenticationResult.supportsPat}
                    scmId={showScmAuthModal.authenticationResult.scmId}
                    scmName={showScmAuthModal.authenticationResult.scmName}
                    onClose={() => setShowScmAuthModal(undefined)}
                    onClickContinue={() =>
                        createEnvironment(
                            showScmAuthModal.project,
                            showScmAuthModal.environmentClass.id,
                            showScmAuthModal.rememberChoice,
                            true,
                        )
                    }
                    runnerId={showScmAuthModal.environmentClass.runnerId}
                    data-track-location={TrackLocations.CreateEnvironmentSCMAuthenticationModal}
                />
            )}
        </>
    );
};
